import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Pagination,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputAdornment
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";

import { api } from "../context/AuthContext";
import {
  Visibility,
  Code,
  Psychology,
  Group,
  Info,
  Search,
  FilterList,
} from "@mui/icons-material";

function InterviewHistory() {

  const [allInterviews, setAllInterviews] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const navigate = useNavigate();
  const [now, setNow] = useState(new Date());
  const [page, setPage] = useState(1);


  useEffect(() => {
    async function fetchHistory() {
      try {
        const response = await api.get(
            "/applications/candidate/applications",
        );
        const allData = Array.isArray(response.data) ? response.data : [];

        setAllInterviews(allData);
      } catch (error) {
        console.error("Failed to fetch interview history:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchHistory();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const getInterviewStatus = (scheduledAt, expiryAt) => {
    const now = new Date();

    const start = new Date(scheduledAt);

    // fallback safety
    const end = expiryAt
      ? new Date(expiryAt)
      : new Date(start.getTime() + 24 * 60 * 60 * 1000);

    // before interview start
    if (now < start) {
      return "not_started";
    }

    // interview active
    if (now >= start && now <= end) {
      return "active";
    }

    // after expiry
    return "expired";
  };

  const getTimeLeft = (scheduledAt) => {
    const start = new Date(scheduledAt);
    const diff = start - now;

    // ✅ show 00:00 before activating
    if (diff <= 0) {
      return "00:00";
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / 60000);
    const secs = Math.floor((diff % 60000) / 1000);

    return `${hours.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const handleOpenDetails = (app) => {
    setSelectedApp(app);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedApp(null);
  };



  if (loading) {
    return (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
    );
  }

  function getStatusColor(status) {
    const s = status?.toLowerCase() || '';

    // Exact colors from the recruiter flow design
    if (s.includes('selected')) return { bg: '#ebfbee', text: '#2f855a' }; // Light green
    if (s.includes('offer released')) return { bg: '#ebf8ff', text: '#2b6cb0' }; // Light blue
    if (s.includes('terminated')) return { bg: '#fff5f5', text: '#c53030' }; // Light red
    if (s.includes('hr scheduled') || s.includes('hr_scheduled') || s.includes('ai_hr_interview') || s.includes('hr_l2_interview')) return { bg: '#EFF6FF', text: '#1E3A8A' }; // Light purple
    if (s.includes('technical cancelled') || s.includes('technical_cancelled')) return { bg: '#fffaf0', text: '#c05621' }; // Light orange
    if (s.includes('ai coding scheduled')) return { bg: '#fffff0', text: '#b7791f' }; // Light yellow

    // Map existing statuses
    if (s.includes('pending')) return { bg: '#fff8e1', text: '#f57f17' };
    if (s.includes('shortlisted')) return { bg: '#e3f2fd', text: '#1976d2' };
    if (s.includes('rejected')) return { bg: '#ffebee', text: '#d32f2f' };
    if (s.includes('completed')) return { bg: '#e0f2f1', text: '#1E40AF' };
    if (s.includes('scheduled')) return { bg: '#e3f2fd', text: '#1976d2' };

    return { bg: '#f5f5f5', text: '#616161' }; // Grey default
  }

    const STATUS_DISPLAY = {
        ai_coding_scheduled: "AI Coding Assessment Scheduled",

        ai_technical_interview_scheduled: "AI Technical Interview Scheduled",
        ai_technical_interview_completed: "AI Technical Interview Completed",

        technical_l2_interview_scheduled: "Technical Round 2 Scheduled",
        technical_l2_interview_completed: "Technical Round 2 Completed",
        technical_l2_interview_cancelled: "Technical Round 2 Cancelled",

        ai_hr_interview_scheduled: "AI HR Interview Scheduled",
        ai_hr_interview_completed: "AI HR Interview Completed",

        hr_l2_interview_scheduled: "HR Round 2 Scheduled",
        hr_l2_interview_completed: "HR Round 2 Completed",
        hr_l2_interview_cancelled: "HR Round 2 Cancelled",

        technical_scheduled: "Technical Interview Scheduled",
        technical_completed: "Technical Interview Completed",
        technical_cancelled: "Technical Interview Cancelled",

        hr_scheduled: "HR Interview Scheduled",
        hr_completed: "HR Interview Completed",
        hr_cancelled: "HR Interview Cancelled",

        candidate_selected: "Selected",
        candidate_rejected: "Rejected",
        offer_released: "Offer Released",
    };

  const formatStatus = (status, jobType) => {
    if (!status) return "";

    const isBPO = jobType === "non_technical_bpo";

    if (isBPO) {
        const bpoStatuses = {
            ai_technical_interview_scheduled:
                "AI Communication & Customer Support Assessment Scheduled",

            ai_technical_interview_completed:
                "AI Communication & Customer Support Assessment Completed",

            technical_l2_interview_scheduled:
                "Customer Support L2 Interview Scheduled",

            technical_l2_interview_completed:
                "Customer Support L2 Interview Completed",

            technical_l2_interview_cancelled:
                "Customer Support L2 Interview Cancelled",

            ai_hr_interview_scheduled:
                "Operations & HR Evaluation Scheduled",

            ai_hr_interview_completed:
                "Operations & HR Evaluation Completed",

            hr_l2_interview_scheduled:
                "Operations & HR Evaluation L2 Interview Scheduled",

            hr_l2_interview_completed:
                "Operations & HR Evaluation L2 Interview Completed",

            hr_l2_interview_cancelled:
                "Operations & HR Evaluation L2 Interview Cancelled",
        };

      if (bpoStatuses[status]) {
        return bpoStatuses[status];
      }
    }

    if (STATUS_DISPLAY[status]) return STATUS_DISPLAY[status];

    return status
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
  };
  const getFilterStatusLabel = (status) => {
    const s = (status || "").toLowerCase();

    if (s.includes("pending")) return "Pending";
    if (s.includes("shortlisted") || s === "application_shortlisted") return "Shortlisted";
    if (s.includes("selected") || s.includes("candidate_selected")) return "Selected";
    if (s.includes("offer released") || s.includes("offer_released") || s.includes("offered")) return "Offer Released";
    if (s.includes("rejected")) return "Rejected";
    if (s.includes("terminated")) return "Terminated";
    if (s.includes("completed")) return "Completed";
    if (s.includes("ai coding scheduled") || s.includes("ai_coding_scheduled")) return "AI Coding Assessment Scheduled";
    if (s.includes("scheduled")) return "Interview Scheduled";
    if (s.includes("technical cancelled") || s.includes("technical_cancelled")) return "Technical Interview Cancelled";
    if (s.includes("hr cancelled") || s.includes("hr_cancelled")) return "HR Interview Cancelled";


    return "All";
  };

   const STATUS_OPTIONS = [
            "All",
            "Pending",
            "Shortlisted",
            "Selected",
            "Offer Released",
            "Rejected",
            "Terminated",
            "Completed",
            "Interview Scheduled",
            "Technical Interview Cancelled",
            "HR Interview Cancelled",
            "AI Coding Assessment Scheduled",
          ];


const filteredInterviews = allInterviews.filter((app) => {
  const title = app.job?.title?.toLowerCase() || "";
  const company = app.job?.company_name?.toLowerCase() || "";
  const search = searchTerm.toLowerCase();

  const matchesSearch = title.includes(search) || company.includes(search);
  const matchesStatus =
    statusFilter === "All" ||
    getFilterStatusLabel(app.status) === statusFilter;

  return matchesSearch && matchesStatus;
});
  const paginatedInterviews =
    filteredInterviews.slice(
      (page - 1) * 10,
      page * 10
    );

   const totalPages =
        Math.ceil(filteredInterviews.length / 10);

  const renderInterviewCard = (type, data) => {
    if (!data) return null;

    const scheduledDate = data.scheduled_at || data.scheduledAt;
    const completedDate = data.completed_at || data.completedAt;
    const appStatus = selectedApp?.status;

    const isAI = type === "CODING" || type === "AI_TECHNICAL" || type === "AI_HR";

    const expiryDate = isAI
      ? (selectedApp?.interview_expiry_at || selectedApp?.interviewExpiryAt)
      : null;

    const status = scheduledDate
      ? getInterviewStatus(scheduledDate, expiryDate)
      : "pending";

    const isTerminated = appStatus === "terminated" || appStatus === "ai_coding_terminated";

    const DOWNSTREAM = ["candidate_selected", "offered", "offer_released"];

    const completedByStatus = {
      // NOTE: coding_tests.completed_at is never set by the backend on AI submission.
      // The application status is the sole source of truth for coding completion.
      CODING: [
        "ai_coding_completed",
      ],
      AI_TECHNICAL: [
        "ai_technical_interview_completed",
      ],
      TECHNICAL_L2: [
        "technical_l2_interview_completed",
      ],
      AI_HR: [
        "ai_hr_interview_completed",
      ],
      HR_L2: [
        "hr_l2_interview_completed",
      ],
    };

    const isStageCompleted = !!completedDate || (completedByStatus[type] || []).includes(appStatus);

    let displayStatus = "Pending";
    if (isTerminated) displayStatus = "Terminated";
    else if (isStageCompleted) displayStatus = "Completed";
    else if (isAI && status === "expired") displayStatus = "Expired";
    else if (scheduledDate) displayStatus = "Scheduled";

    const typeConfig = {
      CODING:       { title: "AI Coding Assessment",   icon: <Code /> },
      AI_TECHNICAL: { title: "AI Technical Interview", icon: <Psychology /> },
      TECHNICAL_L2: { title: "Technical L2 Interview", icon: <Psychology /> },
      AI_HR:        { title: "AI HR Interview",         icon: <Group /> },
      HR_L2:        { title: "HR L2 Interview",         icon: <Group /> },
    };

      const isBPO = selectedApp?.job?.job_type === "non_technical_bpo";

      let title = typeConfig[type].title;
      const icon = typeConfig[type].icon;

      if (isBPO) {
          if (type === "AI_TECHNICAL") {
              title = "AI Communication & Customer Support Assessment";
          }

          if (type === "TECHNICAL_L2") {
              title = "Customer Support L2 Interview";
          }

          if (type === "AI_HR") {
              title = "Operations & HR Evaluation";
          }

          if (type === "HR_L2") {
              title = "Operations & HR Evaluation L2 Interview";
          }
      }

    let interviewLink = null;
    let interviewPath = null;

    if (type === "CODING") {
      const codingToken = data.interview_token || selectedApp?.id;
      interviewLink = `${window.location.origin}/candidate/ai-interview/${codingToken}`;
      interviewPath = `/candidate/ai-interview/${codingToken}`;
    } else if (type === "AI_TECHNICAL" && data.interview_token) {
      interviewLink = `${window.location.origin}/candidate/ai-technical-interview/${data.interview_token}`;
      interviewPath = `/candidate/ai-technical-interview/${data.interview_token}`;
    } else if (type === "AI_HR" && data.interview_token) {
      interviewLink = `${window.location.origin}/candidate/ai-hr-interview/${data.interview_token}`;
      interviewPath = `/candidate/ai-hr-interview/${data.interview_token}`;
    } else if (type === "TECHNICAL_L2" || type === "HR_L2") {
      interviewLink = data.meeting_link || null;
    }

    const showAccessSection =
      (type === "AI_TECHNICAL" || type === "AI_HR" || type === "CODING") &&
      scheduledDate &&
      !isStageCompleted &&
      !isTerminated &&
      (!isAI || status !== "expired");

    return (
      <Grid item xs={12} md={4}>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            height: "100%",
            bgcolor: "#fcfcfc"
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
            <Box sx={{ color: "primary.main", mr: 1 }}>{icon}</Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {title}
            </Typography>
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {/* STATUS */}
            <Typography variant="body2">
              <span style={{ fontWeight: "bold", marginRight: "4px" }}>
                Status:
              </span>
              <span
                style={{
                  color:
                    displayStatus === "Expired" || displayStatus === "Terminated"
                      ? "red"
                      : "inherit",
                  fontWeight:
                    displayStatus === "Expired" || displayStatus === "Terminated"
                      ? "bold"
                      : "normal",
                }}
              >
                {displayStatus}
              </span>
            </Typography>

            {/* DATE + TIME */}
            {scheduledDate && (
              <Typography variant="body2">
                <span style={{ fontWeight: "bold", marginRight: "4px" }}>
                  Scheduled Date:
                </span>
                {new Date(scheduledDate).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Typography>
            )}

            {/* EXPIRED MESSAGE (AI only) */}
            {isAI && status === "expired" && !isStageCompleted && (
              <Typography variant="body2" sx={{ color: "red", fontWeight: "bold" }}>
                The AI Interview got expired
              </Typography>
            )}

            {/* COUNTDOWN */}
            {status === "not_started" && scheduledDate && !isStageCompleted && (
              <Typography variant="body2" color="error">
                Starts in: {getTimeLeft(scheduledDate)}
              </Typography>
            )}

            {/* LINK + BUTTON */}
            {showAccessSection && interviewLink && (
              <>
                <Typography
                  variant="body2"
                  sx={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
                >
                  <span style={{ fontWeight: "bold" }}>Interview Link:</span>{" "}
                  {interviewLink}
                </Typography>

                {interviewPath ? (
                  <Button
                    variant="contained"
                    sx={{ mt: 1 }}
                    disabled={status === "expired" || isTerminated}
                    onClick={() => navigate(interviewPath)}
                  >
                    {status === "not_started"
                      ? `Access Interview (${getTimeLeft(scheduledDate)})`
                      : "Start AI Interview"}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    sx={{ mt: 1 }}
                    onClick={() => window.open(interviewLink, "_blank")}
                  >
                    Access Interview
                  </Button>
                )}
              </>
            )}
          </Box>
        </Paper>
      </Grid>
    );
  };
  return (
      <Container maxWidth="xl" sx={{ py: 4, minHeight: '100vh' }}>

      {/* Top Countdown Banner */}
      {(() => {
        const upcomingInterview = allInterviews.find(app => {
          const data = app.codingTest;

          if (!data) return false;

          const scheduled =
            data.interview_scheduled_at ||
            data.scheduled_at;

          const expiry =
            app.interview_expiry_at ||
            app.interviewExpiryAt;

          return (
            getInterviewStatus(scheduled, expiry) ===
            "not_started"
          );
        });

        if (!upcomingInterview) return null;

        const codingTest = upcomingInterview.codingTest;

        const scheduled =
          codingTest.interview_scheduled_at ||
          codingTest.scheduled_at;

        return (
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              p: 2,
              borderRadius: '12px',
              bgcolor: "#f0f7ff",
              border: "1px solid #eff6ff"
            }}
          >
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#1e293b'
              }}
            >
              Your{" "}
              <strong>
                AI Coding Interview
              </strong>{" "}
              for the role of{" "}
              <strong>
                {upcomingInterview.job?.title}
              </strong>{" "}
              at{" "}
              <strong>
                {upcomingInterview.job?.company_name}
              </strong>{" "}
              is scheduled to begin in{" "}
              <span
                style={{
                  color: "#1E3A8A",
                  fontWeight: "bold"
                }}
              >
                {getTimeLeft(scheduled)}
              </span>
              .
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: "text.secondary"
              }}
            >
              Please ensure your camera,
              microphone, and internet
              connection are ready before
              starting the interview.
              Best wishes for your assessment.
            </Typography>
          </Paper>
        );
      })()}

        <Box sx={{ mb: 4 }}>

          {/* Top Row */}
          <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
                flexWrap: "wrap",
              }}
          >

            {/* Left Side */}
            <Box>
              <Typography
                  variant="h4"
                  sx={{
                    color: "#1E3A8A",
                    fontWeight: "bold",
                    fontSize: { xs: "1.5rem", md: "2rem" },
                  }}
              >
                Interview History
              </Typography>

              <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
              >
                Track the status of all your interviews.
              </Typography>
            </Box>

            {/* Right Side */}
            <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
            >
              <TextField
                  size="small"
                  placeholder="Search by role or company..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  sx={{
                    width: { xs: "100%", sm: 320, md: 360 },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "50px",
                      bgcolor: "white",
                      "& fieldset": {
                        borderColor: "#e2e8f0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#cbd5e0",
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: "#a0aec0" }} />
                        </InputAdornment>
                    ),
                  }}
              />

              <FormControl size="small" sx={{ minWidth: 145 }}>
                <Select
                    displayEmpty
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                    }}
                    renderValue={() => (
                        <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              color: "#1E3A8A",
                              fontWeight: "bold",
                            }}
                        >
                          <FilterList />
                          Filters
                        </Box>
                    )}
                    sx={{
                      borderRadius: "10px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e2e8f0",
                      },
                    }}
                >
                  <MenuItem
                      disabled
                      sx={{
                        fontWeight: 700,
                        color: "#1E3A8A",
                        borderBottom: "1px solid #e5e7eb",
                        justifyContent: "center",
                      }}
                  >
                    Filters
                  </MenuItem>

                  {STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

        </Box>
        <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: '1px solid #e5e7eb',
              borderRadius: '18px',
              overflowX: 'auto',
              boxShadow: '0 6px 18px rgba(0,0,0,0.05)',
            }}
        >
          <Table sx={{ minWidth: 650 }}>
            <TableHead sx={{ bgcolor: "#f8f9fa" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", color: "#1a202c", py: 2 }}>Job Title</TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "#1a202c", py: 2 }}>Company</TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "#1a202c", py: 2 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "#1a202c", py: 2 }}>Date Applied</TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "#1a202c", py: 2 }} align="right">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedInterviews.map((app) => {
                  const statusStyles = getStatusColor(app.status);
                  return (
                    <TableRow key={app.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell sx={{ py: 2.5 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ color: '#1E3A8A', fontWeight: 'bold' }}
                        >
                          {app.job?.title}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: '#1a202c', fontWeight: 500 }}>{app.job?.company_name}</TableCell>
                      <TableCell>
                        <Chip
                            label={formatStatus(
                                app.status,
                                app.job?.job_type
                            )}
                            size="small"
                            sx={{
                              fontWeight: 'bold',
                              borderRadius: "4px",
                              px: 1,
                              height: "24px",
                              fontSize: "0.75rem",
                              backgroundColor: statusStyles.bg,
                              color: statusStyles.text,
                            }}
                        />
                      </TableCell>

                      <TableCell sx={{ color: '#1a202c', fontSize: '0.875rem' }}>
                        {new Date(
                            app.created_at || app.createdAt,
                        ).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Visibility sx={{ fontSize: '16px !important' }} />}
                            onClick={() => handleOpenDetails(app)}
                            sx={{ 
                              textTransform: "none", 
                              fontWeight: 'bold',
                              color: '#1E3A8A',
                              borderColor: '#1E3A8A',
                              borderRadius: '8px',
                              '&:hover': { 
                                borderColor: '#1E40AF', 
                                bgcolor: 'rgba(30,58,138,0.04)' 
                              }
                            }}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
              })}
              {paginatedInterviews.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <Typography color="text.secondary">
                        No interview records found
                      </Typography>
                    </TableCell>
                  </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {paginatedInterviews.length > 0 ? (page - 1) * 10 + 1 : 0} to{" "}
            {Math.min(page * 10, filteredInterviews.length)} of{" "}
            {filteredInterviews.length} applications
          </Typography>

          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => {
              setPage(value);
              window.scrollTo(0, 0);
            }}
            shape="rounded"
            color="primary"
            sx={{
              "& .MuiPaginationItem-root.Mui-selected": {
                backgroundColor: "#1E3A8A",
                "&:hover": {
                  backgroundColor: "#1E40AF",
                },
              },
            }}
          />
        </Box>

        {/* Details Dialog */}
        <Dialog
            open={detailsDialogOpen}
            onClose={handleCloseDetails}
            maxWidth="md"
            fullWidth
        >
          <DialogTitle
              sx={{
                bgcolor: "#1E3A8A",
                color: "white",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Interview Details</Typography>
              <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                {selectedApp?.job?.title} at {selectedApp?.job?.company_name}
              </Typography>
            </Box>
            <Chip
                label={formatStatus(
                    selectedApp?.status,
                    selectedApp?.job?.job_type
                )}
                sx={{ bgcolor: "white", color: "#1E3A8A", fontWeight: "bold", borderRadius: '4px' }}
            />
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              {/* Overview Card */}
              <Grid item xs={12}>
                <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "flex-start", sm: "center" },
                      gap: { xs: 2, sm: 4 },
                      bgcolor: "#f8f9fa",
                    }}
                >
                  <Box>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                    >
                      Current Status
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {(() => {
                        const appStatus = selectedApp?.status;
                        const isCoding = appStatus === "ai_coding_completed" || appStatus === "ai_technical_interview_completed";
                        const isTechnical = appStatus === "technical_scheduled" || appStatus === "technical_completed" || appStatus === "technical_interview_completed" || appStatus === "technical_l2_interview_scheduled" || appStatus === "technical_l2_interview_completed" || appStatus === "ai_technical_interview_scheduled" || appStatus === "ai_technical_interview_completed";
                        const isHR = appStatus === "hr_scheduled" || appStatus === "hr_completed" || appStatus === "hr_interview_completed" || appStatus === "ai_hr_interview_scheduled" || appStatus === "ai_hr_interview_completed" || appStatus === "hr_l2_interview_scheduled" || appStatus === "hr_l2_interview_completed";
                        const isSelected = appStatus === "candidate_selected" || appStatus === "offered" || appStatus === "offer_released";
                        const isCanceled = appStatus === "rejected";

                        if (isCanceled) return "Canceled";
                        if (isSelected || appStatus === "hr_completed") return "Completed";
                        if (appStatus?.includes("scheduled")) return "Scheduled";
                        
                        return formatStatus(selectedApp?.current_stage || selectedApp?.currentStage);
                      })()}
                    </Typography>
                  </Box>
                  <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ display: { xs: "none", sm: "block" } }}
                  />
                  <Box>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                    >
                      Applied On
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedApp &&
                          new Date(
                              selectedApp.created_at || selectedApp.createdAt,
                          ).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ display: { xs: "none", sm: "block" } }}
                  />
                  <Box>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                    >
                      Application ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                      {selectedApp?.id}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              {/* Assessment Sections */}
                {selectedApp?.job?.job_type !== "non_technical_bpo" &&
                    renderInterviewCard("CODING", selectedApp?.codingTest)}

              {selectedApp?.technicalInterview &&
                renderInterviewCard(
                  selectedApp.technicalInterview.technical_interview_type === "AI_TECHNICAL"
                    ? "AI_TECHNICAL"
                    : "TECHNICAL_L2",
                  selectedApp.technicalInterview,
                )}

              {selectedApp?.hrInterview &&
                renderInterviewCard(
                  selectedApp.hrInterview.interview_token ? "AI_HR" : "HR_L2",
                  selectedApp.hrInterview,
                )}

              {!selectedApp?.codingTest &&
                  !selectedApp?.technicalInterview &&
                  !selectedApp?.hrInterview && (
                      <Grid item xs={12}>
                        <Box
                            sx={{
                              textAlign: "center",
                              py: 4,
                              bgcolor: "#f9f9f9",
                              borderRadius: 2,
                              border: "1px dashed #ccc",
                            }}
                        >
                          <Info
                              sx={{ color: "text.secondary", fontSize: 40, mb: 1 }}
                          />
                          <Typography color="text.secondary">
                            No interview stages have been scheduled yet.
                          </Typography>
                        </Box>
                      </Grid>
                  )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseDetails} variant="outlined">
              Close
            </Button>
            <Button
                variant="contained"
                onClick={() => navigate("/candidate/dashboard")}
            >
              Go to Dashboard
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
  );
}

export default InterviewHistory;
